export const reports = {
  // Dashboard
  dashboard: {
    title: 'Reports Dashboard',
    subtitle: 'Access comprehensive reports and analytics for your EV charging network',
    noReports: 'No reports available',
    noReportsDesc: 'You do not have permission to access any reports',
  },

  // Categories
  categories: {
    financial: 'Financial Reports',
    financialDesc: 'Revenue tracking, transaction analysis, and billing reports',
    operational: 'Operational Reports',
    operationalDesc: 'Session analytics, utilization metrics, and energy consumption',
    vendor: 'Vendor Reports',
    vendorDesc: 'Cross-vendor performance comparison and site analytics',
  },

  // Revenue Analysis Report
  revenue: {
    title: 'Revenue Analysis',
    description: 'Track revenue trends by time period with site/charger/vendor breakdowns',
    subtitle: 'Revenue performance and financial metrics',
  },

  // Transaction Volume Report
  transactions: {
    title: 'Transaction Volume',
    description: 'Analyze transaction patterns and payment methods',
    subtitle: 'Transaction analysis and payment trends',
  },

  // Transaction (singular for labels)
  transaction: {
    title: 'Transaction',
  },

  // Session Analytics Report
  sessions: {
    title: 'Session Analytics',
    description: 'Deep dive into charging session patterns and usage',
    subtitle: 'Detailed charging session insights',
  },

  // Charger Utilization Report
  utilization: {
    title: 'Charger Utilization',
    description: 'Track charger usage efficiency and uptime',
    subtitle: 'Charger performance and availability metrics',
  },

  // Energy Consumption Report
  energy: {
    title: 'Energy Consumption',
    description: 'Track energy delivery patterns and cost breakdown',
    subtitle: 'Energy usage and cost analysis',
    roundingNote: 'Note: Actual Billed Cost represents the sum of actual billed sessions. Due to standard currency rounding at the individual session level, multiplying Total Energy by the Avg Cost per kWh may yield a minor fractional variance.',
    energyByWindowTitle: 'Energy by Charging Window',
  },

  // Peak Usage Report
  peak: {
    title: 'Peak Usage Analysis',
    description: 'Identify peak usage patterns for capacity planning',
    subtitle: 'Load distribution and peak demand analysis',
  },

  // Vendor Performance Report
  vendorPerformance: {
    title: 'Vendor Performance',
    description: 'Compare performance metrics across all vendors',
    subtitle: 'Cross-vendor performance comparison',
  },

  // Site Comparison Report
  siteComparison: {
    title: 'Site Comparison',
    description: 'Compare all sites across vendors',
    subtitle: 'Site-level performance analysis',
  },

  // User Activity Report
  userActivity: {
    title: 'User Activity',
    description:
      'Charging activity aggregated per user (App, RFID, Vendor API, Backend) across your sites — sessions, duration, energy, revenue, and contact info for follow-up.',
    searchLabel: 'Search user (name / phone)',
    searchPlaceholder: 'Type a name or phone',
    userTypesLabel: 'User types',
    userTypeAll: 'All',
    userType: {
      app: 'App',
      rfid: 'RFID',
      vendor_api: 'Vendor API',
      backend: 'Backend',
    },
    empty: 'No users match the current filters.',
    exportTruncated:
      'Export was truncated to the first %{count} rows. Narrow the date range to see more.',
    exportFailed: 'Export failed — see console.',
    kpi: {
      totalUsers: 'Total Users',
      totalUsersSubtitle: 'Active users',
      totalSessions: 'Total Sessions',
      chargedSessions: 'Charged Sessions',
      avgPerUser: '%{count} avg/user',
      totalEnergy: 'Total Energy',
      totalRevenue: 'Total Revenue',
      avgRevenuePerUser: 'HKD %{amount} avg/user',
    },
    fields: {
      phone: 'Phone',
      email: 'Email',
      carPlate: 'Car Plate',
      sessions: 'Sessions',
      duration: 'Duration',
      energy: 'Energy',
      lastSession: 'Last session',
    },
    table: {
      user: 'User',
      type: 'Type',
      contact: 'Contact',
      sessions: 'Sessions',
      chargedSessions: 'Charged Sessions',
      duration: 'Duration',
      energy: 'Energy (kWh)',
      revenue: 'Revenue (HKD)',
      sites: 'Sites',
      lastSession: 'Last session',
    },
  },

  // Filters
  filters: {
    title: 'Filter Conditions',
    startDate: 'Start Date',
    endDate: 'End Date',
    dateRange: 'Date Range',
    site: 'Site',
    allSites: 'All Sites',
    vendor: 'Vendor',
    allVendors: 'All Vendors',
    charger: 'Charger',
    allChargers: 'All Chargers',
    groupBy: 'Group By',
    custom: 'Custom',
  },

  // Group By Options
  groupBy: {
    day: 'Day',
    week: 'Week',
    month: 'Month',
    site: 'Site',
    charger: 'Charger',
    method: 'Initiation Method',
  },

  // Tabs
  tabs: {
    charts: 'Charts',
    dataTable: 'Data Table',
    timeSeries: 'Time Series',
  },

  // Actions
  actions: {
    refresh: 'Refresh',
    export: 'Export',
    viewDetails: 'View Details',
    downloadReport: 'Download Report',
  },

  // Export
  export: {
    csv: 'Export CSV',
    excel: 'Export Excel',
    pdf: 'Export PDF',
    csvSuccess: 'CSV exported successfully',
    excelSuccess: 'Excel exported successfully',
    csvFailed: 'CSV export failed — see console for details.',
    excelFailed: 'Excel export failed — see console for details.',
    noData: 'No data to export — adjust filters and try again.',
  },

  // Excel Export
  excel: {
    generated: 'Generated',
  },

  // Errors
  errors: {
    loadFailed: 'Failed to load report data',
    exportFailed: 'Failed to export report',
    noData: 'No data available for the selected period',
    invalidDateRange: 'Invalid date range selected',
  },


  // KPI Cards
  kpi: {
    totalRevenue: 'Total Revenue',
    totalSessions: 'Total Sessions',
    chargedSessions: 'Charged Sessions',
    chargedSessionsTooltip: 'Sessions that actually delivered energy (kWh > 0). Sessions where the vehicle plugged in but no energy was delivered (for example, immediate unplug or fault) are excluded.',
    totalSessionsTooltip: 'All archived sessions in the selected date range, including those that delivered no energy.',
    avgRevenuePerSession: 'Avg Revenue Per Session',
    totalEnergy: 'Total Energy',
    totalEnergyKwh: 'Total Energy (kWh)',
    totalEnergyCost: 'Total Energy Cost',
    totalIdleCost: 'Total Idle Cost',
    totalCost: 'Total Cost',
    totalTransactions: 'Total Transactions',
    totalValue: 'Total Value',
    totalAmount: 'Total Amount',
    successRate: 'Success Rate',
    avgTransactionAmount: 'Avg Transaction Amount',
    completedTransactions: 'Completed',
    completedCount: 'Completed Count',
    failedTransactions: 'Failed',
    utilizationRate: 'Utilization Rate',
    uptimeRate: 'Uptime Rate',
    sessionsPerDay: 'Sessions Per Day',
    totalChargers: 'Total Chargers',
    availableChargers: 'Available',
    offlineChargers: 'Offline/Faulted',
    avgDuration: 'Average Duration',
    avgEnergy: 'Average Energy',
    completionRate: 'Completion Rate',
    avgCostPerKwh: 'Avg Cost per kWh',
    theoreticalCost: 'Theoretical Cost',
    roundingVariance: 'Rounding Variance',
    actualBilledCost: 'Actual Billed Cost',
    peakEnergyPercent: 'Peak Energy %',
    peakHour: 'Peak Hour',
    peakHourSessions: 'Peak Hour Sessions',
    peakConcurrentSessions: 'Peak Concurrent Sessions',
    peakLoad: 'Peak Load (kW)',
    totalVendors: 'Total Vendors',
    avgUptime: 'Avg Uptime',
    totalSites: 'Total Sites',
    activeSites: 'Active Sites',
    avgUtilization: 'Avg Utilization',
    bestPerformer: 'Best Performer',
  },

  // Chart Titles
  charts: {
    revenueOverTime: 'Revenue Over Time',
    revenueBySite: 'Revenue by Site (Top 10)',
    revenueByMethod: 'Revenue by Initiation Method',
    transactionVolumeOverTime: 'Transaction Volume Over Time',
    transactionsByType: 'Transactions by Type',
    transactionsByMethod: 'Transactions by Payment Method',
    paymentMethodDistribution: 'Payment Method Distribution',
    dailyTransactionVolume: 'Daily Transaction Volume',
    sessionsOverTime: 'Sessions Over Time',
    sessionsBySite: 'Sessions by Site',
    sessionsByMethod: 'Sessions by Initiation Method',
    hourlyUsagePattern: 'Hourly Usage Pattern',
    utilizationByCharger: 'Utilization by Charger',
    uptimeTrends: 'Uptime Trends',
    utilizationTrends: 'Utilization Trends',
    statusDistribution: 'Charger Status Distribution',
    energyOverTime: 'Energy Delivered Over Time',
    energyByTimeWindow: 'Energy by Time Window',
    costBreakdown: 'Cost Breakdown',
    hourlyDistribution: 'Hourly Distribution',
    peakConcurrentSessions: 'Peak Concurrent Sessions',
    vendorComparison: 'Vendor Comparison',
    sitePerformance: 'Site Performance',
    vendorSessions: 'Sessions by Vendor',
    vendorRevenue: 'Revenue by Vendor',
    revenueTrends: 'Revenue Trends by Vendor',
    topSitesSessions: 'Top Sites by Sessions',
    topSitesRevenue: 'Top Sites by Revenue',
  },

  // Time Periods
  periods: {
    today: 'Today',
    yesterday: 'Yesterday',
    last7Days: 'Last 7 Days',
    last30Days: 'Last 30 Days',
    thisMonth: 'This Month',
    lastMonth: 'Last Month',
    custom: 'Custom Range',
  },

  // Initiation Methods
  methods: {
    app: 'Mobile App',
    rfid: 'RFID Card',
    admin: 'Admin',
    ocpp: 'OCPP',
    vendorApi: 'Vendor API',
  },

  // Transaction Types
  transactionTypes: {
    topup: 'Top Up',
    payment: 'Payment',
    refund: 'Refund',
    credit: 'Credit',
  },

  // Payment Methods
  paymentMethods: {
    wallet: 'Wallet',
    credit_card: 'Credit Card',
    rfid_billing: 'RFID Billing',
  },

  // Transaction Statuses
  statuses: {
    completed: 'Completed',
    pending: 'Pending',
    failed: 'Failed',
  },

  // Loading and Empty States
  loading: 'Loading report data...',
  noData: 'No data available for the selected period',
  selectDateRange: 'Please select a date range to view report',

  // Table View
  dailyBreakdown: 'Daily Breakdown',
  summary: 'Summary',
  days: 'days',
  table: {
    date: 'Date',
    month: 'Month',
    hour: 'Hour',
    revenue: 'Revenue',
    sessions: 'Sessions',
    avgPerSession: 'Avg Per Session',
    avgDuration: 'Avg Duration (min)',
    energyKwh: 'Energy (kWh)',
    energyCost: 'Energy Cost',
    idleCost: 'Idle Cost',
    completionRate: 'Completion Rate %',
    chargingHours: 'Charging Hours',
    cost: 'Cost',
    avgCostPerKwh: 'Avg Cost/kWh',
    count: 'Count',
    transactionCount: 'Count',
    completed: 'Completed',
    successRate: 'Success Rate',
    amount: 'Amount',
    total: 'Total',
    average: 'Average',
    vendor: 'Vendor',
    type: 'Type',
    uptime: 'Uptime %',
    sites: 'Sites',
    chargers: 'Chargers',
    site: 'Site',
    location: 'Location',
    utilization: 'Utilization %',
    availability: 'Availability %',
    charger: 'Charger',
    sessionsPerDay: 'Sessions/Day',
    status: 'Status',
  },

  // Other labels
  chargerDetails: 'Charger Details',
  vendorDetails: 'Detailed Vendor Metrics',
  siteDetails: 'Detailed Site Metrics',

  // Hong Kong Locations - Districts and Areas
  locations: {
    // Hong Kong Island Districts
    'central-western': 'Central & Western',
    'central-and-western': 'Central & Western',
    'eastern': 'Eastern',
    'southern': 'Southern',
    'wan-chai': 'Wan Chai',

    // Kowloon Districts
    'kowloon-city': 'Kowloon City',
    'yau-tsim-mong': 'Yau Tsim Mong',
    'sham-shui-po': 'Sham Shui Po',
    'wong-tai-sin': 'Wong Tai Sin',
    'kwun-tong': 'Kwun Tong',

    // New Territories Districts
    'tai-po': 'Tai Po',
    'yuen-long': 'Yuen Long',
    'tuen-mun': 'Tuen Mun',
    'north': 'North',
    'sai-kung': 'Sai Kung',
    'sha-tin': 'Sha Tin',
    'tsuen-wan': 'Tsuen Wan',
    'kwai-tsing': 'Kwai Tsing',
    'islands': 'Islands',

    // Popular Areas - Hong Kong Island
    'central': 'Central',
    'admiralty': 'Admiralty',
    'sheung-wan': 'Sheung Wan',
    'mid-levels': 'Mid-Levels',
    'peak': 'The Peak',
    'causeway-bay': 'Causeway Bay',
    'north-point': 'North Point',
    'quarry-bay': 'Quarry Bay',
    'shau-kei-wan': 'Shau Kei Wan',
    'chai-wan': 'Chai Wan',
    'aberdeen': 'Aberdeen',
    'stanley': 'Stanley',
    'repulse-bay': 'Repulse Bay',
    'tin-hau': 'Tin Hau',
    'fortress-hill': 'Fortress Hill',
    'tai-koo': 'Tai Koo',
    'sai-wan-ho': 'Sai Wan Ho',

    // Popular Areas - Kowloon
    'tsim-sha-tsui': 'Tsim Sha Tsui',
    'jordan': 'Jordan',
    'yau-ma-tei': 'Yau Ma Tei',
    'mong-kok': 'Mong Kok',
    'prince-edward': 'Prince Edward',
    'tai-kok-tsui': 'Tai Kok Tsui',
    'to-kwa-wan': 'To Kwa Wan',
    'hung-hom': 'Hung Hom',
    'kowloon-bay': 'Kowloon Bay',
    'kwun-tong-area': 'Kwun Tong',
    'ngau-tau-kok': 'Ngau Tau Kok',
    'diamond-hill': 'Diamond Hill',
    'choi-hung': 'Choi Hung',
    'lok-fu': 'Lok Fu',
    'kowloon-tong': 'Kowloon Tong',
    'lam-tin': 'Lam Tin',
    'yau-tong': 'Yau Tong',

    // Popular Areas - New Territories
    'tsing-yi': 'Tsing Yi',
    'kwai-chung': 'Kwai Chung',
    'tsing-lung-tau': 'Tsing Lung Tau',
    'tai-wo-hau': 'Tai Wo Hau',
    'shek-kip-mei': 'Shek Kip Mei',
    'mei-foo': 'Mei Foo',
    'lai-chi-kok': 'Lai Chi Kok',
    'cheung-sha-wan': 'Cheung Sha Wan',
    'shatin': 'Sha Tin',
    'ma-on-shan': 'Ma On Shan',
    'tai-wai': 'Tai Wai',
    'fo-tan': 'Fo Tan',
    'tseung-kwan-o': 'Tseung Kwan O',
    'clear-water-bay': 'Clear Water Bay',
    'sai-kung-town': 'Sai Kung Town',
    'fanling': 'Fanling',
    'sheung-shui': 'Sheung Shui',
    'tai-po-market': 'Tai Po Market',
    'tuen-mun-town': 'Tuen Mun Town',
    'yuen-long-town': 'Yuen Long Town',
    'tin-shui-wai': 'Tin Shui Wai',
    'tung-chung': 'Tung Chung',
    'discovery-bay': 'Discovery Bay',
    'mui-wo': 'Mui Wo',
    'cheung-chau': 'Cheung Chau',
    'lamma-island': 'Lamma Island',
    'lantau-island': 'Lantau Island',
  },
};
